// Port of ios/Sources/ViewModels/RebuildPacing.swift.
//
// How long a screen must wait before rebuilding itself again — derived from what the LAST rebuild
// actually cost, not from a constant chosen on a small account.
//
// The Swift twin of `android/.../viewmodels/RebuildPacing.kt`, written for the same measurement:
// one reader here is a member of **2,410 conversations** (1,533 of them WhatsApp-bridge channels
// in a single team, plus 354 DMs and 213 group messages — counted on the server, 2026-09-23), and
// the sidebar rebuild reads every one of them, their member rows, their latest posts, their
// drafts and their participants.
//
// A fixed coalescing window assumes that rebuild is cheap. At this size it is not, and because
// the bridge writes posts continuously there is always another request pending by the time one
// finishes — so the loop runs flat out and the machine never gets to be idle. On Android that is
// a phone getting hot in the hand; on the Mac it is a fan and a battery, since a desktop app is
// never suspended the way iOS suspends one.
//
// The rule is that **work paces itself by its own cost**: after a rebuild that took `d`, wait at
// least `d` before the next. The derivation can then never occupy more than half of wall clock,
// at any list size, without anyone guessing a number. Small accounts are unaffected — `d` is a
// few milliseconds and the floor still decides.
//
// WEB: nanoseconds kept as in Swift (plain numbers; 2e9 is far inside 2^53). A caller timing
// with `performance.now()` (ms) multiplies by 1e6.

/** Freshness floor: how quickly the list may follow a change on a cheap account. */
export const floorNanos = 150_000_000;

/**
 * The longest this will ever hold a pending rebuild back. Past this the list is stale enough
 * that the reader notices, and the rebuild itself is the thing to fix.
 */
export const ceilingNanos = 2_000_000_000;

/**
 * @param lastRebuildNanos how long the rebuild that just finished took.
 * @returns how long to wait before honouring the next pending request.
 */
export function nextDelayNanos(lastRebuildNanos: number, floor: number = floorNanos, ceiling: number = ceilingNanos): number {
  return Math.min(Math.max(lastRebuildNanos, floor), ceiling);
}

/** Namespace form, for `RebuildPacing.nextDelayNanos(...)` call sites. */
export const RebuildPacing = { floorNanos, ceilingNanos, nextDelayNanos } as const;
